import type { LayoutRectangle } from 'react-native'

// React Native can ignore or overshoot scroll requests at the exact content edge.
// Keeping the target one pixel before the end gives the desktop slider a stable last offset.
const getMaxScrollableX = ({
  innerContainerWidth,
  scrollViewWidth,
}: {
  innerContainerWidth: number
  scrollViewWidth: number
}): number => Math.max(0, innerContainerWidth - scrollViewWidth - 1)

// Auto-scroll targets an item x position, but items near the end cannot be aligned
// to the left without asking past the content bounds.
export const getAutoScrollTargetX = ({
  targetItemX,
  innerContainerWidth,
  scrollViewWidth,
}: {
  targetItemX: number
  innerContainerWidth: number
  scrollViewWidth: number
}): number =>
  innerContainerWidth > scrollViewWidth
    ? Math.min(targetItemX, getMaxScrollableX({ innerContainerWidth, scrollViewWidth }))
    : 0

export const setLayoutAtIndex = (
  coordinates: LayoutRectangle[],
  index: number,
  layout: LayoutRectangle,
): LayoutRectangle[] => {
  const newCoordinates = [...coordinates]
  newCoordinates[index] = layout
  return newCoordinates
}

export const getAutoScrollTarget = ({
  autoScrollIndex,
  coordinates,
  itemCount,
}: {
  autoScrollIndex?: number
  coordinates: LayoutRectangle[]
  itemCount: number
}): { coordinate: LayoutRectangle; index: number } | undefined => {
  if (autoScrollIndex === undefined || autoScrollIndex < 0 || autoScrollIndex >= itemCount) return undefined

  const coordinate = coordinates[autoScrollIndex]
  return coordinate ? { coordinate, index: autoScrollIndex } : undefined
}

// Arrow navigation should only scroll once the target item has reported its layout.
export const getPreviousScrollTarget = ({
  selectedScrollIndex,
  coordinates,
}: {
  selectedScrollIndex: number
  coordinates: LayoutRectangle[]
}): { index: number; x: number } | undefined => {
  const index = Math.max(0, selectedScrollIndex - 1)
  const coordinate = coordinates[index]

  return coordinate ? { index, x: index === 0 ? 0 : coordinate.x } : undefined
}

// Find the last item that can still be aligned to the left while leaving content
// to scroll through on its right.
const getMaxItemIndexForScrolling = ({
  coordinates,
  innerContainerWidth,
  scrollViewWidth,
}: {
  coordinates: LayoutRectangle[]
  innerContainerWidth: number
  scrollViewWidth: number
}): number => {
  for (let index = coordinates.length - 1; index >= 0; index--) {
    const item = coordinates[index]
    if (innerContainerWidth - item.x > scrollViewWidth) return index
  }

  return -1
}

// When the next item is past the last left-alignable item, scroll to the final
// content offset instead of trying to align an item that cannot fit at the left edge.
export const getNextScrollTarget = ({
  selectedScrollIndex,
  coordinates,
  innerContainerWidth,
  scrollViewWidth,
}: {
  selectedScrollIndex: number
  coordinates: LayoutRectangle[]
  innerContainerWidth: number
  scrollViewWidth: number
}): { index: number; x: number } | undefined => {
  const maxItemIndexForScrolling = getMaxItemIndexForScrolling({ coordinates, innerContainerWidth, scrollViewWidth })
  const lastReachableIndex = maxItemIndexForScrolling + 1
  if (selectedScrollIndex >= lastReachableIndex) return undefined

  const index = Math.min(selectedScrollIndex + 1, lastReachableIndex)

  if (!coordinates[index]) return undefined

  return {
    index,
    x:
      index > maxItemIndexForScrolling
        ? getMaxScrollableX({ innerContainerWidth, scrollViewWidth })
        : coordinates[index].x,
  }
}
